import { Box, Grid, StyleProps } from '@chakra-ui/react';
import {
	addDays,
	addWeeks,
	differenceInDays,
	endOfMonth,
	format,
	isSameDay,
	isSameMonth,
	isSaturday,
	isSunday,
	isToday,
	isWithinInterval,
	nextSaturday,
	parse,
	previousSunday,
	startOfDay,
	startOfMonth,
} from 'date-fns';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@bonusly/ds-typography';
import { rem } from '@bonusly/ds-utils';
import { assertPresence } from '@bonusly/runtime';

import { useDatePickerContext } from '../context';

export type CalendarProps = StyleProps;

const dateId = (date: Date) => 'date-' + format(date, 'y-M-d');
const DATA_DATE_ATTR = 'data-date';
const findDateEl = (date: Date | null) => {
	if (!date) {
		return null;
	}

	return document.getElementById(dateId(date));
};
const focusDate = (date: Date) => {
	const el = findDateEl(date);
	el?.focus();
};

export const Calendar: FC<CalendarProps> = (props) => {
	const { t } = useTranslation('components');
	const { disableDateIf, month, onHoverDate, onSelectDate, selectionEnd, selectionStart } = useDatePickerContext();

	// Keyboard navigation using arrow keys to move around the calendar. It will not switch months if you reach the limits of the displayed month.
	// The user must use the page up and page down keys to switch months.
	useEffect(() => {
		const listener = (e: KeyboardEvent) => {
			switch (e.key) {
				case 'Home':
					return focusDate(startOfMonth(month));
				case 'End':
					return focusDate(endOfMonth(month));
			}

			const today = new Date();
			let defaultDate = isSameMonth(today, month) ? today : month;

			if (selectionStart) {
				defaultDate = selectionStart;
			} else if (selectionEnd) {
				defaultDate = selectionEnd;
			}

			let dir = 0;

			switch (e.key) {
				case 'ArrowLeft':
					dir = -1;
					break;
				case 'ArrowRight':
					dir = 1;
					break;
				case 'ArrowUp':
					dir = -7;
					break;
				case 'ArrowDown':
					dir = 7;
					break;
			}

			if (!dir) {
				return;
			}

			const defaultEl = document.getElementById(dateId(defaultDate));

			if (!document.activeElement?.getAttribute(DATA_DATE_ATTR)) {
				return defaultEl?.focus();
			}

			try {
				const dateString = document.activeElement.getAttribute(DATA_DATE_ATTR);
				assertPresence(dateString);
				const date = addDays(parse(dateString, 'y-M-d', new Date()), dir);
				const el = document.getElementById(dateId(date));
				el?.focus();
			} catch {
				return defaultEl?.focus();
			}
		};

		document.addEventListener('keydown', listener);

		return () => {
			document.removeEventListener('keydown', listener);
		};
	}, [month, selectionEnd, selectionStart]);

	const days = useMemo(() => {
		const firstOfMonth = startOfMonth(month ?? new Date());
		const lastOfMonth = startOfDay(endOfMonth(firstOfMonth));
		const firstOfRange = previousSunday(firstOfMonth);
		let lastOfRange = nextSaturday(lastOfMonth);

		const totalDays = differenceInDays(lastOfRange, firstOfRange) + 1;

		if (totalDays !== 42) {
			lastOfRange = addWeeks(lastOfRange, 1);
		}

		return Array.from({ length: differenceInDays(lastOfRange, firstOfRange) + 1 }, (_, index) => {
			return addDays(firstOfRange, index);
		});
	}, [month]);

	const totalRange = (selectionStart && selectionEnd && differenceInDays(selectionEnd, selectionStart) + 1) || 1;

	return (
		<Grid maxW={rem(280)} rowGap={1} templateColumns="repeat(7, 1fr)" {...props}>
			{new Array(7).fill('').map((_, date) => (
				<Text color="text.softer" fontWeight="semibold" key={date} pb={1} pt={3} textAlign="center">
					{t('date_picker.day_of_week', { date: new Date(2001, 0, date) })}
				</Text>
			))}
			{days.map((date) => {
				return (
					<Day
						date={date}
						disableDateIf={disableDateIf}
						key={format(date, 'd-M-y')}
						month={month}
						onHoverDate={onHoverDate}
						onSelectDate={onSelectDate}
						selectionEnd={selectionEnd}
						selectionStart={selectionStart}
						totalRange={totalRange}
					/>
				);
			})}
		</Grid>
	);
};

type DayProps = {
	date: Date;
	disableDateIf?: (date: Date) => boolean;
	month?: Date;
	onHoverDate?: (date: Date) => void;
	onSelectDate?: (date: Date) => void;
	selectionEnd?: Date | null;
	selectionStart?: Date | null;
	totalRange: number;
};

const Day: FC<DayProps> = ({ date, disableDateIf, month, onHoverDate, onSelectDate, selectionEnd, selectionStart, totalRange }) => {
	const { t } = useTranslation('components');
	const isCurrentMonth = isSameMonth(date, month ?? new Date());
	const today = isToday(date);
	const isSelectedStart = selectionStart && isSameDay(date, selectionStart);
	const isSelectedEnd = selectionEnd && isSameDay(date, selectionEnd);
	const isSelected = isSelectedStart || isSelectedEnd;
	const isDisabled = disableDateIf?.(date) ?? false;

	const isWithinSelection = selectionStart && selectionEnd && isWithinInterval(date, { start: selectionStart, end: selectionEnd });

	let textColor = 'text.default';

	if (isSelected) {
		textColor = 'secondary.fgOnSurface';
	} else if (!isCurrentMonth) {
		textColor = 'text.disabled';
	}

	const handleClick = () => {
		onSelectDate?.(date);
	};

	const handleHover = () => {
		onHoverDate?.(date);
	};

	return (
		<Box
			position="relative"
			{...((isWithinSelection || isSelected) &&
				totalRange > 1 && {
					backgroundColor: 'secondary.bgAlpha',
					// TODO: Adjust styling for selection mode
					...((isSunday(date) || isSelectedStart) && {
						borderTopLeftRadius: 'full',
						borderBottomLeftRadius: 'full',
					}),
					...((isSaturday(date) || isSelectedEnd) && {
						borderTopRightRadius: 'full',
						borderBottomRightRadius: 'full',
					}),
				})}
		>
			<Box
				alignItems="center"
				aria-label={t('date_picker.date_value_long', { date })}
				as="button"
				borderRadius="full"
				boxSize={rem(40)}
				color={textColor}
				data-date={format(date, 'y-M-d')}
				id={`date-${format(date, 'y-M-d')}`}
				display="flex"
				justifyContent="center"
				onClick={handleClick}
				disabled={isDisabled}
				onMouseEnter={handleHover}
				pos="relative"
				transitionDuration="fast"
				transitionProperty="background-color"
				type="button"
				_focus={{
					backgroundColor: 'secondary.bgAlpha',
				}}
				_hover={{
					backgroundColor: 'secondary.bgAlpha',
				}}
				_disabled={{
					color: 'text.disabled',
					backgroundColor: 'transparent',
					cursor: 'not-allowed',
				}}
				_selected={{
					backgroundColor: 'secondary.bg',
					_active: {
						backgroundColor: 'secondary.bgActive',
					},
					_hover: {
						backgroundColor: 'secondary.bgHover',
					},
				}}
				{...(today && {
					_after: {
						backgroundColor: 'text.interactive',
						borderRadius: 'full',
						boxSize: 1,
						content: '""',
						pos: 'absolute',
						top: rem(31),
					},
				})}
				{...(isSelected && {
					'data-selected': true,
				})}
			>
				<Text>{format(date, 'd')}</Text>
			</Box>
		</Box>
	);
};
